<script>

    import * as echarts from 'echarts'
    import Moment from 'moment'
    import { onMount } from 'svelte'

    import GraphicsService from '../$services/graphics.service'
    import NumeralMethods from '../$methods/numeral.methods'

    export let dateStart = null
    export let dateEnd = null

    const TOP_N = 5
    const COLORS = ['#098E37', '#3b82f6', '#f59e0b', '#38bdf8', '#8b5cf6', '#64748b']

    let chartContainer
    let chart
    let ready = false
    let total = 0
    let items = []

    $: periodLabel = dateStart && dateEnd
        ? `${Moment(dateStart).locale('es').format('D MMM YYYY')} - ${Moment(dateEnd).locale('es').format('D MMM YYYY')}`
        : ''

    onMount(() => {
        chart = echarts.init(chartContainer)
        ready = true

        const resize = () => chart.resize()
        window.addEventListener('resize', resize)

        return () => {
            window.removeEventListener('resize', resize)
            chart.dispose()
            chart = null
            ready = false
        }
    })

    $: if (ready) load(dateStart, dateEnd)

    function mapProvider(item) {
        return {
            provider: item.provider,
            partner: item.partner || '',
            name: item.partner ? `${item.provider} · ${item.partner}` : item.provider,
            total: item.total,
            percent: item.percent,
        }
    }

    function groupProviders(providers) {
        const top = providers.slice(0, TOP_N).map(mapProvider)
        const rest = providers.slice(TOP_N)

        if (!rest.length) return top

        const otherTotal = rest.reduce((sum, p) => sum + (p.total || 0), 0)
        const otherPercent = rest.reduce((sum, p) => sum + (p.percent || 0), 0)

        return [
            ...top,
            {
                provider: 'Otros',
                partner: '',
                name: 'Otros',
                total: otherTotal,
                percent: Math.round(otherPercent * 10) / 10,
            },
        ]
    }

    async function load(start, end) {
        if (!chart || !start || !end) return

        const res = await GraphicsService.getShipmentsProviders({
            dateStart: start.toISOString(),
            dateEnd: end.toISOString(),
        })

        const data = res?.data ?? res
        total = data?.total ?? 0
        items = groupProviders(data?.providers ?? [])

        chart.setOption({
            color: COLORS,
            tooltip: {
                trigger: 'item',
                formatter: (params) => `${params.name}: ${params.value} (${params.percent}%)`,
            },
            series: [
                {
                    type: 'pie',
                    radius: ['62%', '82%'],
                    center: ['50%', '50%'],
                    avoidLabelOverlap: false,
                    label: { show: false },
                    labelLine: { show: false },
                    itemStyle: {
                        borderRadius: 4,
                        borderColor: '#fff',
                        borderWidth: 3,
                    },
                    data: items.map((item) => ({
                        name: item.name,
                        value: item.total,
                    })),
                },
            ],
            graphic: [
                {
                    type: 'text',
                    left: 'center',
                    top: '42%',
                    style: {
                        text: 'Total',
                        fill: '#9e9e9e',
                        fontSize: 13,
                        fontWeight: 500,
                        textAlign: 'center',
                    },
                },
                {
                    type: 'text',
                    left: 'center',
                    top: '52%',
                    style: {
                        text: NumeralMethods.number(total),
                        fill: '#1a1a1a',
                        fontSize: 22,
                        fontWeight: 700,
                        textAlign: 'center',
                    },
                },
            ],
        }, true)
    }

</script>

<style>
    .card {
        background: #fff;
        border-radius: 16px;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
        padding: 1.35rem 1.5rem;
        height: 100%;
        display: flex;
        flex-direction: column;
    }

    .header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 1rem;
        margin-bottom: 1rem;
    }

    .title {
        font-size: 1.05rem;
        font-weight: 700;
        color: #1a1a1a;
        margin: 0 0 0.25rem;
        line-height: 1.2;
    }

    .period {
        font-size: 0.85rem;
        color: #9e9e9e;
        margin: 0;
        text-transform: capitalize;
    }

    .link {
        color: #22c55e;
        font-size: 0.9rem;
        font-weight: 600;
        text-decoration: none;
        white-space: nowrap;
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
    }

    .link:hover {
        color: #16a34a;
    }

    .link :global(i) {
        font-size: 0.7rem;
    }

    .body {
        display: flex;
        align-items: center;
        gap: 1.25rem;
        flex: 1;
        min-height: 0;
    }

    .chart {
        width: 200px;
        height: 200px;
        flex-shrink: 0;
    }

    .legend {
        list-style: none;
        margin: 0;
        padding: 0;
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 0.55rem;
    }

    .legend li {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 0.75rem;
        font-size: 0.85rem;
        line-height: 1.2;
    }

    .name {
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;
        min-width: 0;
    }

    .dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        flex-shrink: 0;
        margin-top: 0.25rem;
    }

    .label {
        min-width: 0;
    }

    .provider {
        display: block;
        color: #1a1a1a;
        font-weight: 600;
    }

    .partner {
        display: block;
        color: #9e9e9e;
        font-weight: 400;
        font-size: 0.75rem;
    }

    .meta {
        color: #6b7280;
        white-space: nowrap;
        font-weight: 500;
        flex-shrink: 0;
    }

    .meta strong {
        color: #1a1a1a;
        font-weight: 600;
    }
</style>

<div class="card">
    <div class="header">
        <div>
            <h3 class="title">Envíos por paquetería</h3>
            {#if periodLabel}
                <p class="period">{ periodLabel }</p>
            {/if}
        </div>
        <a class="link" href="#/providers">
            Ver todos
            <i class="fas fa-arrow-right"></i>
        </a>
    </div>

    <div class="body">
        <div bind:this={chartContainer} class="chart"></div>

        <ul class="legend">
            {#each items as item, i}
                <li>
                    <span class="name">
                        <span class="dot" style="background: {COLORS[i] || COLORS[COLORS.length - 1]}"></span>
                        <span class="label">
                            <span class="provider">{ item.provider }</span>
                            {#if item.partner}
                                <span class="partner">{ item.partner }</span>
                            {/if}
                        </span>
                    </span>
                    <span class="meta">
                        <strong>{ NumeralMethods.number(item.total) }</strong>
                        ({ item.percent }%)
                    </span>
                </li>
            {/each}
        </ul>
    </div>
</div>
